import { CRM_SUITE_ENTERPRISE_BUNDLE, CRM_SUITE_PROFESSIONAL_BUNDLE, CRM_SUITE_STARTER_BUNDLE, FIRST_FULL_TIME_MIGRATION, MARKETING_PLUS_ENTERPRISE_BUNDLE, MARKETING_PLUS_PROFESSIONAL_BUNDLE } from 'self-service-api/constants/ApiNames';
import * as CtaTypes from '../constants/CtaTypes';
import * as Languages from '../constants/Languages';
import { BUNDLE } from '../constants/NonMerchandiseIdProducts';
import { langToUrl } from './urls';

// (may 8, 2019) cta documentation:
// https://docs.google.com/spreadsheets/d/1BHY9NGG6e41cna8s31RnxY1Sn0I7kNVBzzwuAxVd2D0/edit?ts=5cd1f219#gid=1017927284

const apiNameToSignupStringMap = {
  [FIRST_FULL_TIME_MIGRATION]: 'cmsmigrations',
  [CRM_SUITE_STARTER_BUNDLE]: 'crm-suite-starter',
  [CRM_SUITE_PROFESSIONAL_BUNDLE]: 'crm-suite-professional',
  [CRM_SUITE_ENTERPRISE_BUNDLE]: 'crm-suite-enterprise',
  [MARKETING_PLUS_PROFESSIONAL_BUNDLE]: 'marketing-plus-professional',
  [MARKETING_PLUS_ENTERPRISE_BUNDLE]: 'marketing-plus-enterprise'
};

/**
 * hubs_signup-cta should be unique to each purchase motion.  It can be derived from
 * sourceId and the nonMerchandiseIdProduct so we centralize it here rather than having
 * each purchase motion explicitly define it.
 *
 * The format should be: {product line page}-{pricing page identifier}-{product}{productlevel}
 *
 * So for the MH Starter "buy now" CTA on the MH Starter page, the string would be something like
 *    ?hubs_signup-url=www.hubspot.com/pricing/marketing/starter&hubs_signup-cta=marketing-pricing-mhstarter
 *
 * For the CRM Suite Starter "buy now" CTA on the MH Starter page, the string would be something like,
 *    ?hubs_signup-url=www.hubspot.com/pricing/marketing/starter&hubs_signup-cta=marketing-pricing-crmstarter
 */
export const getHubsSignupCTA = ({
  ctaType,
  sourceId,
  nonMerchandiseIdProduct,
  productLine,
  productLevel,
  apiName,
  pathname
}) => {
  let resolvedProductIdentifier;
  let resolvedSourceIdentifier;
  if (!nonMerchandiseIdProduct && !productLine && !(apiName && apiName in apiNameToSignupStringMap)) {
    throw new Error('need one of (nonMerchandiseIdProduct) or (productLine) or (apiName that is present in apiNameToSignupStringMap)');
  }
  const currentPage = pathname.split('/')[2] || pathname.split('/')[1];
  if (ctaType === CtaTypes.BUY || ctaType === CtaTypes.LEARN_MORE || ctaType === CtaTypes.TRIAL) {
    const sourceParts = sourceId.split('-');
    resolvedSourceIdentifier = `${sourceParts[0]}-${ctaType}${sourceParts[1]}`;
  } else {
    resolvedSourceIdentifier = sourceId;
  }
  if (nonMerchandiseIdProduct) {
    resolvedProductIdentifier = nonMerchandiseIdProduct;
  } else if (apiName && apiName in apiNameToSignupStringMap) {
    resolvedProductIdentifier = apiNameToSignupStringMap[apiName];
  } else if (productLine) {
    resolvedProductIdentifier = `${productLine.toLowerCase()}${productLevel ? productLevel.toLowerCase() : ''}`;
  }
  return `${currentPage}-${resolvedSourceIdentifier}-${resolvedProductIdentifier}`;
};

// hubs_signup-url is added as a query param to any CTAs on the public pricing page  for the the marketing
// team's GA  tracking. New CTAs can be added just in a manner consistent to other ones, but we should be
// mindful of editing existing ones as it could break tracking and we should consult with web strategy first
export const getHubsSignupUrl = ({
  nonMerchandiseIdProduct,
  baseUrl
}) => {
  baseUrl = baseUrl || `${window.location.hostname}${window.location.pathname}`;

  // Strip query params from this URL
  let url = encodeURI(baseUrl.split('?')[0]);
  if (nonMerchandiseIdProduct && nonMerchandiseIdProduct === BUNDLE) {
    // Pass whole url (including query params) for bundles
    url = encodeURI(baseUrl);
  }
  return url;
};

// This function exists to simply wrap classNames so we are more aware that
// they are necessary for Google Analytics tracking and don't remove them accidentally
export const getGAClassName = gaTrackingClassname => gaTrackingClassname;

// need to keep a check on this - not sure if will be accurate for all links
export const getPublicSiteDomain = () => {
  const langMap = {
    [Languages.EN]: 'https://www.hubspot.com',
    [Languages.DE]: 'https://www.hubspot.de',
    [Languages.ES]: 'https://www.hubspot.es',
    [Languages.PT]: 'https://br.hubspot.com',
    [Languages.FR]: 'https://www.hubspot.fr',
    [Languages.JA]: 'https://www.hubspot.jp'
  };
  return langToUrl(langMap);
};
export const getStartedCmsUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/marketing/content-management-system/get-started`;
};
export const getMarketingUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/marketing`;
};
export const getMarketingPricingUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/pricing/marketing`;
};
export const getSalesUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/sales`;
};
export const getServiceUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/service`;
};
export const getCMSUrlPublic = isUngatedForContentHub => {
  const publicSiteDomain = getPublicSiteDomain();
  return isUngatedForContentHub ? `${publicSiteDomain}/products/content` : `${publicSiteDomain}/products/cms`;
};
export const getCommerceUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/pricing/commerce`;
};
export const getWebsiteMigrationUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/services/professional/migrations/website-migration`;
};
export const getCMSOnboardingUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/services/onboarding/cms`;
};
export const getOperationsUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/operations`;
};
export const getOnboardingServicesUrlPublic = isForStartups => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/services/onboarding/${isForStartups ? 'startup' : ''}`;
};
export const getPaymentsUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/payments`;
};
export const getStartupsUrlPublic = sourceId => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/startups?source=dot-com-pricing&hubs_content=${window.location.href.split('?')[0]}&hubs_content-cta=${getHubsSignupCTA({
    pathname: '/startups',
    ctaType: CtaTypes.LEARN_MORE,
    sourceId,
    nonMerchandiseIdProduct: 'hsfs'
  })}`;
};
export const getSmartCrmAIUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/products/artificial-intelligence?hubs_content=www.hubspot.com/products/crm/ai-crm&hubs_content-cta=smartcrm-faq-breeze`;
};
export const getSmartCrmPricingUrlPublic = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/pricing/breeze-intelligence?hubs_content=www.hubspot.com/products/crm/ai-crm&hubs_content-cta=smartcrm-faq-breezeintelpricing`;
};
export const getNonprofitsUrlPublic = sourceId => {
  // Localized url does not exist yet
  return `https://www.hubspot.com/nonprofits?source=dot-com-pricing&hubs_content=${window.location.href.split('?')[0]}&hubs_content-cta=${getHubsSignupCTA({
    pathname: '/nonprofits',
    ctaType: CtaTypes.LEARN_MORE,
    sourceId,
    nonMerchandiseIdProduct: 'hsfn'
  })}`;
};
export const getProductAndServicesUrlPublic = () => {
  const langMap = {
    [Languages.EN]: 'https://legal.hubspot.com/hubspot-product-and-services-catalog',
    [Languages.DE]: 'https://legal.hubspot.com/de/hubspot-product-and-services-catalog',
    [Languages.ES]: 'https://legal.hubspot.com/es/hubspot-product-and-services-catalog',
    [Languages.PT]: 'https://legal.hubspot.com/br/hubspot-product-and-services-catalog',
    [Languages.FR]: 'https://legal.hubspot.com/fr/hubspot-product-and-services-catalog',
    [Languages.JA]: 'https://legal.hubspot.com/jp/hubspot-product-and-services-catalog'
  };
  return langToUrl(langMap);
};
export const getKnowledgeBaseUrlPublic = () => {
  const langMap = {
    [Languages.EN]: 'https://knowledge.hubspot.com',
    [Languages.DE]: 'https://knowledge.hubspot.com/de',
    [Languages.ES]: 'https://knowledge.hubspot.com/es',
    [Languages.PT]: 'https://knowledge.hubspot.com/pt',
    [Languages.FR]: 'https://knowledge.hubspot.com/fr',
    [Languages.JA]: 'https://knowledge.hubspot.com/jp'
  };
  return langToUrl(langMap);
};
export const getCommunityUrlPublic = () => {
  const langMap = {
    [Languages.EN]: 'https://community.hubspot.com',
    [Languages.DE]: 'https://community.hubspot.com/?profile.language=de',
    [Languages.ES]: 'https://community.hubspot.com/?profile.language=es',
    [Languages.PT]: 'https://community.hubspot.com/?profile.language=pt-br',
    [Languages.FR]: 'https://community.hubspot.com/?profile.language=fr',
    [Languages.JA]: 'https://community.hubspot.com/?profile.language=ja'
  };
  return langToUrl(langMap);
};
export const getAcademyUrlPublic = () => {
  const langMap = {
    [Languages.EN]: 'https://academy.hubspot.com',
    [Languages.DE]: 'https://academy.hubspot.de',
    [Languages.ES]: 'https://academy.hubspot.com/es',
    [Languages.PT]: 'https://academy.hubspot.com/pt',
    [Languages.FR]: 'https://academy.hubspot.fr',
    [Languages.JA]: 'https://academy.hubspot.jp'
  };
  return langToUrl(langMap);
};
export const getLanguagesUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/account/hubspot-language-offerings`;
};
export const getKBPaymentsUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/payments/payments-frequently-asked-questions`;
};
export const getKBShareFormsUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/forms/how-can-i-share-a-hubspot-form-if-im-using-an-external-site`;
};
export const getKBEmbedCTAUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/ctas/add-ctas-to-your-pages`;
};
export const getKBTrackingCodeUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/reports/install-the-hubspot-tracking-code`;
};
export const getKBImportToolUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/blog/import-your-content-into-hubspot`;
};
export const getMicrositesExamplesUrl = () => {
  return 'https://designers.hubspot.com/inspire/browse?functionality=Microsite';
};
export const getKBMembershipsUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/website-pages/require-member-registration-to-access-private-content`;
};
export const getKBStripeCurrencyUrlPublic = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/payments/use-multiple-currencies-with-stripe`;
};
export const getKBExportContentAndData = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/account-management/export-your-content-and-data`;
};
export const getKBExportRecords = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/import-and-export/export-records`;
};
export const getKBExportAdPerformanceData = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/ads/export-your-ads-campaign-data`;
};
export const getKBExportMarketingEmailPerformanceData = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/marketing-email/how-do-i-export-my-overall-email-performance-data`;
};
export const getKBPermanentDelete = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/privacy-and-consent/how-do-i-perform-a-gdpr-delete-in-hubspot`;
};
export const getKBBreezeIntelligenceFAQ = () => {
  const publicKBDomain = getKnowledgeBaseUrlPublic();
  return `${publicKBDomain}/privacy-and-consent/breeze-intelligence-faq`;
};
export const getMembershipsExamplesUrl = () => {
  return 'https://designers.hubspot.com/inspire/browse?functionality=Memberships+%2F+Login';
};
export const PAYMENTS_LANDING_PAGE_URL = 'https://www.hubspot.com/products/payments';
export const getTtsUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://offers.hubspot.com/contact-sales',
    [Languages.DE]: 'https://offers.hubspot.de/contact-sales',
    [Languages.ES]: 'https://offers.hubspot.es/contact-sales',
    [Languages.PT]: 'https://br.hubspot.com/ofertas/contact-sales',
    [Languages.FR]: 'https://offers.hubspot.fr/contact-sales',
    [Languages.JA]: 'https://offers.hubspot.jp/contact-sales'
  };
  return langToUrl(langMap);
};
export const getOperationsSupportedApplicationsUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://ecosystem.hubspot.com/marketplace/apps?eco_hubSpotFeatures=CRM_DATA_SYNC',
    [Languages.DE]: 'https://ecosystem.hubspot.com/de/marketplace/apps?eco_hubSpotFeatures=CRM_DATA_SYNC',
    [Languages.ES]: 'https://ecosystem.hubspot.com/es/marketplace/apps?eco_hubSpotFeatures=CRM_DATA_SYNC',
    [Languages.PT]: 'https://ecosystem.hubspot.com/pt/marketplace/apps?eco_hubSpotFeatures=CRM_DATA_SYNC',
    [Languages.FR]: 'https://ecosystem.hubspot.com/fr/marketplace/apps?eco_hubSpotFeatures=CRM_DATA_SYNC',
    [Languages.JA]: 'https://ecosystem.hubspot.com/ja/marketplace/apps?eco_hubSpotFeatures=CRM_DATA_SYNC'
  };
  return langToUrl(langMap);
};
export const getPartnerProgramUrl = () => {
  const publicSiteDomain = getPublicSiteDomain();
  return `${publicSiteDomain}/partners/solutions`;
};
export const getMarketPlaceBaseUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://ecosystem.hubspot.com/marketplace',
    [Languages.DE]: 'https://ecosystem.hubspot.com/de/marketplace',
    [Languages.ES]: 'https://ecosystem.hubspot.com/es/marketplace',
    [Languages.PT]: 'https://ecosystem.hubspot.com/pt/marketplace',
    [Languages.FR]: 'https://ecosystem.hubspot.com/fr/marketplace',
    [Languages.JA]: 'https://ecosystem.hubspot.com/ja/marketplace'
  };
  return langToUrl(langMap);
};
export const getAppMarketplaceUrl = () => {
  const marketPlaceUrl = getMarketPlaceBaseUrl();
  return `${marketPlaceUrl}/apps`;
};
export const getTemplatesMarketplaceUrl = () => {
  const marketPlaceUrl = getMarketPlaceBaseUrl();
  return `${marketPlaceUrl}/templates`;
};
export const getSolutionsMarketplaceUrl = () => {
  const marketPlaceUrl = getMarketPlaceBaseUrl();
  return `${marketPlaceUrl}/solutions`;
};
export const getDevelopersAndApiDocsBaseUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://developers.hubspot.com',
    [Languages.DE]: 'https://developers.hubspot.de',
    [Languages.ES]: 'https://developers.hubspot.es',
    [Languages.PT]: 'https://br.developers.hubspot.com',
    [Languages.FR]: 'https://developers.hubspot.fr',
    [Languages.JA]: 'https://developers.hubspot.jp'
  };
  return langToUrl(langMap);
};
export const getDevelopersQuickStartGuideUrl = () => {
  const developersUrl = getDevelopersAndApiDocsBaseUrl();
  return `${developersUrl}/docs/cms/guides/getting-started`;
};
export const getDevelopersApiDocsUrl = () => {
  const developersUrl = getDevelopersAndApiDocsBaseUrl();
  return `${developersUrl}/docs/api/overview`;
};
const getLegalBaseUrl = () => {
  const langMap = {
    [Languages.EN]: 'https://legal.hubspot.com',
    [Languages.DE]: 'https://legal.hubspot.com/de',
    [Languages.ES]: 'https://legal.hubspot.com/es',
    [Languages.PT]: 'https://legal.hubspot.com/br',
    [Languages.FR]: 'https://legal.hubspot.com/fr',
    [Languages.JA]: 'https://legal.hubspot.com/jp'
  };
  return langToUrl(langMap);
};
export const getSecurityAndComplianceUrl = () => {
  const legalUrl = getLegalBaseUrl();
  return `${legalUrl}/security`;
};
export const getPrivacyPolicyUrl = () => {
  const legalUrl = getLegalBaseUrl();
  return `${legalUrl}/privacy-policy`;
};
export const getDataProcessingAgreementUrl = () => {
  const legalUrl = getLegalBaseUrl();
  return `${legalUrl}/dpa`;
};
export const getSolutionsPartnerProgramBaseUrl = () => {
  return `${getPublicSiteDomain()}/solutions-partner-resource-center`;
};
export const getSolutionsPartnerProgramSalesRulesUrl = () => {
  return `${getSolutionsPartnerProgramBaseUrl()}/sales-rules`;
};
export const getPrivacyRequestFormUrl = () => {
  const baseUrl = 'https://preferences.hubspot.com/privacy';
  const langMap = {
    [Languages.EN]: baseUrl,
    [Languages.DE]: `${baseUrl}?locale=de`,
    [Languages.ES]: `${baseUrl}?locale=es-MX`,
    [Languages.PT]: `${baseUrl}?locale=pt-br`,
    [Languages.FR]: `${baseUrl}?locale=fr`,
    [Languages.JA]: `${baseUrl}?locale=ja`
  };
  return langToUrl(langMap);
};
export const getTrustCenterUrl = () => {
  return 'https://trust.hubspot.com/';
};
export const getQBOIntegrationURL = () => {
  const marketPlaceUrl = getMarketPlaceBaseUrl();
  return `${marketPlaceUrl}/apps/finance/accounting/quickbooks-data-sync`;
};